<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .auto-join-settings {
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: utils.in_rem(16px);
    align-items: flex-start;
    width: utils.in_rem(520px);

    .section-title {
      @include mixins.h3();
      @include colors.color(textBlack);
    }

    .auto-join-dropdown-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      flex: 1 0 0;

      .auto-join-dropdown {
        display: flex;
        align-items: center;
        align-self: stretch;
      }
    }
  }
</style>

<script>
  import ButtonWithTooltip from "@sourcewhale/shared/svelte-design-system/components/atoms/ButtonWithTooltip.svelte";
  import Label from "@sourcewhale/shared/svelte-design-system/components/atoms/InputLabels/Label.svelte";
  import Dropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/Dropdown.svelte";
  import ListItem from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/ListItem.svelte";
  import { onDestroy } from "svelte";
  import { writable } from "svelte/store";
  import { showToast } from "@sourcewhale/shared/src/assets/js/src/general/utils";
  import {
    teamNotetakerSettingsStore,
    teamStore,
  } from "../../../../api-stores/meetings-store";
  import { updateNotetakerJoinSettings } from "../../../../api/notetaker";
  import { ToastError } from "@sourcewhale/shared/src/svelte/api/sw-toast-error";

  const adminNotetakerSettingsStore = writable(null);

  const unsubscribe = teamNotetakerSettingsStore.subscribe((settings) => {
    adminNotetakerSettingsStore.set(settings?.joinSettings);
  });

  const autoJoinModes = {
    allMeetings: "All meetings",
    allExternal: "Only meetings with external participants",
    inviteOnly: "Only when Notetaker is invited",
  };

  $: ({ joinMode = "allExternal", locked = false } =
    $adminNotetakerSettingsStore || {});

  const onclick = async (value) => {
    try {
      const res = await updateNotetakerJoinSettings({
        joinSetting: "teamAutoJoinMode",
        joinMode: value,
      });
      adminNotetakerSettingsStore.set(
        res.updateDict.notetakerSettings.joinSettings
      );
      showToast({ type: "success", title: "Saved" });
    } catch (error) {
      if (error instanceof ToastError) {
        showToast(error);
        return;
      }
      throw error;
    } finally {
      teamStore.get();
    }
  };

  const onclickLock = async () => {
    try {
      const res = await updateNotetakerJoinSettings({
        joinSetting: "teamAutoJoinMode",
        locked: !locked,
      });
      const title = `Auto-join setting ${
        locked ? "unlocked" : "locked"
      } for your team`;
      adminNotetakerSettingsStore.set(
        res.updateDict.notetakerSettings.joinSettings
      );
      showToast({
        type: "success",
        title,
      });
    } catch (error) {
      if (error instanceof ToastError) {
        showToast(error);
        return;
      }
      throw error;
    } finally {
      teamStore.get();
    }
  };

  onDestroy(() => {
    unsubscribe();
  });
</script>

<div class="auto-join-settings">
  <span class="section-title">Auto-join settings</span>
  <div class="auto-join-dropdown-wrapper">
    <Label label="Which meetings should Notetaker join?" labelSize="large" />
    <div class="auto-join-dropdown">
      <Dropdown kind="input-field" size="large">
        <span slot="button" class="display-text">{autoJoinModes[joinMode]}</span
        >
        {#each Object.entries(autoJoinModes) as [value, label]}
          <ListItem selected={joinMode === value} onclick={() => onclick(value)}
            >{label}</ListItem
          >
        {/each}
      </Dropdown>
      <ButtonWithTooltip
        leadingIcon="far fa-lock{locked ? '' : '-open'}"
        style="width: 44px; height: 44px;"
        kind="ghost-grey"
        onclick={onclickLock}
      >
        <span slot="tooltip"
          >{`${locked ? "Unlock" : "Lock"} this setting for my team`}</span
        >
      </ButtonWithTooltip>
    </div>
  </div>
</div>
